const EBIS_WO_URL       = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ebis-workorder`
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

export const SHOP_RATE = 145

export function calcLaborCost(hours) {
  return hours != null ? Math.round(hours * SHOP_RATE) : null
}

/**
 * Push items to EBIS as a new quote, new work order, or added to an existing WO.
 *
 * @param {{ items, regNum?, workOrder?, type? }} params
 *   items     — array of { task_number, task_name, labor_hours?, labor_cost?, parts_cost? }
 *   regNum    — aircraft registration (required when creating a new WO/quote)
 *   workOrder — existing WO/quote number (required when adding to existing)
 *   type      — 'quote' (default) | 'work-order'
 *
 * @returns {{ woNumber: string }}
 * @throws  Error — with .code === 'AIRCRAFT_NOT_FOUND' for that specific EBIS condition
 */
const DRY_RUN = true

function cleanItems(items) {
  return items.map(({ task_number, task_name, labor_hours, parts_cost, parts }) =>
    ({ task_number, task_name, labor_hours, parts_cost, parts })
  )
}

export async function pushToEbis({ items, regNum, workOrder, type = 'quote' }) {
  if (!Array.isArray(items) || items.length === 0) throw new Error('No items to push')

  const cleaned = cleanItems(items)

  if (DRY_RUN) {
    const payload = { items: cleaned }
    if (workOrder) payload.workOrder = workOrder
    else { payload.regNum = regNum; payload.type = type }
    const ebisPayload = {
      CityID: 20,
      BillingProfileID: 2223,
      DefaultToAircraftPrimaryCustomer: true,
      EBisMainTypeID: type === 'work-order' ? 1 : 11,
      ...payload,
    }
    console.log('[ebisPush DRY RUN] full EBIS payload:', JSON.stringify(ebisPayload, null, 2))
    return { woNumber: workOrder ?? 'DRY-RUN' }
  }

  const body = { items: cleaned }
  if (workOrder) {
    body.workOrder = workOrder
  } else if (regNum) {
    body.regNum = regNum
    body.type   = type
  } else {
    throw new Error('regNum or workOrder required')
  }

  const r = await fetch(`${EBIS_WO_URL}?endpoint=create-wo`, {
    method:  'POST',
    headers: {
      Authorization:  `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  const text = await r.text()
  let data
  try { data = JSON.parse(text) } catch { data = { raw: text } }
  console.log('[ebisPush] raw response:', JSON.stringify(data, null, 2))

  if (!r.ok) {
    const msg = data?.Message || data?.error || `EBIS returned ${r.status}`
    const err = new Error(msg)
    if (/aircraft/i.test(msg) && /(not found|not exist|no record|invalid|unknown)/i.test(msg)) {
      err.code = 'AIRCRAFT_NOT_FOUND'
    }
    throw err
  }

  // EBIS can return status 200 with validation errors
  if (data?.Data?.MessageID === 'ValidationErrors') {
    throw new Error(`EBIS: ${data.Data.MessageText} — ${(data.Data.InvalidParameters || []).join(', ')}`)
  }

  const woNumber =
    data?.WorkOrder       || data?.QuoteNumber    ||
    data?.Data?.WorkOrder || data?.Data?.WoNumber ||
    (data?.Data?.ID != null ? String(data.Data.ID) : null)

  const woLink = data?.Data?.EBisWoLink || null

  return { woNumber: woNumber ?? (workOrder ?? 'Created'), woLink }
}
